// Story 8.14 (ADO-2023), D-7: the one send, for every Submit button on the Order Review page.
//
//   const { send, blocked, sending } = useSubmitOrder(depotId);
//
// The Submit card (8.6) and the Order footer (8.14) both render a "Submit order" button over
// this hook, so the two share one action (`useOrderSubmissionStore.submit`) and one blocked
// predicate: while an attempt is in flight ("Submitting…"), while a line edit is settling, a
// previous order is being added or the cart is being cleared (Story 8.9), and while the active
// basket is not open for the cart. The cart the Shopper reviewed is the cart that is sent. The
// buttons are `aria-disabled`, not `disabled`, so a screen reader still reaches them; `send` is a
// no-op while `blocked`, and `submit` itself refuses a second attempt, so pressing both buttons
// sends once.
//
// Story 8.10 (ADO-2019), Q2: a send of a cart at an HHT depot, or of the Default VC cart (DV-73),
// while HHT orders wait asks first (`useHhtSendGuardStore`); "Send anyway" then sends as normal.
// The page mounts the dialog.

import { useCallback } from 'react';
import { sameCartTarget } from '../api/cart-target';
import { useHhtSendGuardStore } from '../hht/useHhtSendGuardStore';
import { cartTargetAt } from './sessionCart';
import { useActiveBasketStore } from './useActiveBasketStore';
import { useBasketStore } from './useBasketStore';
import { useOrderSubmissionStore } from './useOrderSubmissionStore';

export interface SubmitOrderControl {
  readonly send: () => void;
  readonly blocked: boolean;
  readonly sending: boolean;
}

export function useSubmitOrder(depotId: string): SubmitOrderControl {
  const phase = useOrderSubmissionStore((s) => (s.depotId === depotId ? s.phase : 'idle'));
  const submit = useOrderSubmissionStore((s) => s.submit);
  const basketSettling = useBasketStore(
    (s) => s.pending.size > 0 || s.addingFromOrder || s.clearing,
  );
  // Story 8.4 (ADO-2013): open for the cart at the depot, so the depot AND the account.
  const basketOpen = useActiveBasketStore(
    (s) =>
      s.status === 'ready' && sameCartTarget(s.cart, cartTargetAt(depotId)) && s.basket !== null,
  );
  const sending = phase === 'sending';
  const blocked = sending || basketSettling || !basketOpen;

  const guard = useHhtSendGuardStore((s) => s.guard);
  const send = useCallback(() => {
    if (blocked) return;
    void guard(cartTargetAt(depotId), () => void submit(depotId));
  }, [blocked, guard, submit, depotId]);

  return { send, blocked, sending };
}
